<div class="list-toolbar">
  <mat-form-field appearance="outline" class="filter" subscriptSizing="dynamic">
    <mat-label>Payment</mat-label>
    <mat-select [(ngModel)]="paymentStatus" (selectionChange)="onFilterChange()">
      <mat-option value="">All</mat-option>
      <mat-option value="NotPayed">Unpaid</mat-option>
      <mat-option value="Payed">Paid</mat-option>
    </mat-select>
  </mat-form-field>
  <mat-checkbox [(ngModel)]="includeZeroCoinRuns" (change)="onFilterChange()">Show 0-coin runs</mat-checkbox>
</div>

@if (!state.supplierId) {
<p class="hint"><mat-icon>info</mat-icon> Filter by a supplier to select all of their unpaid runs at once.</p>
}

@if (canSelectAll && pageAllSelected) {
<div class="select-banner">
  @if (allUnpaidCount !== null) {
  <span>All <b>{{ allUnpaidCount }}</b> unpaid runs of this supplier are selected.</span>
  <button mat-button type="button" (click)="clearSelection()">Clear selection</button>
  } @else {
  <span><b>{{ selectedOnPage }}</b> unpaid runs on this page are selected.</span>
  @if (canSelectAllPages) {
  <button mat-button type="button" [disabled]="selectingAll" (click)="selectAllUnpaid()">
    Select all unpaid runs of this supplier
    @if (selectingAll) { <mat-spinner class="spinner-inline" diameter="16"></mat-spinner> }
  </button>
  }
  }
</div>
}

<div class="table-card">
@if (loading) {
<div class="table-state"><mat-spinner diameter="28"></mat-spinner></div>
} @else if (error) {
<div class="table-state error">{{ error }}</div>
} @else if (runs.length === 0) {
<div class="table-state">No finished transfer runs match these filters.</div>
} @else {

@if (isMobile$ | async) {
@if (canSelectAll) {
<label class="select-all-row">
  <mat-checkbox [checked]="pageAllSelected" [indeterminate]="pageSomeSelected" (change)="togglePage()"></mat-checkbox>
  Select all unpaid on this page
</label>
}
<div class="cards">
  @for (r of runs; track r.id) {
  <div class="card" [class.card-selected]="state.runs.has(r.id)">
    <div class="card-head">
      <mat-checkbox [disabled]="r.payment_status === 'Payed'" [checked]="state.runs.has(r.id)"
        (change)="toggle(r)"></mat-checkbox>
      <div class="card-title">
        <span class="supplier-link" (click)="openSupplierInfoDialog(r.supplier_id, r.supplier_name)">{{ r.supplier_name }}</span>
        <p class="card-meta mono">{{ r.login_info_email }}</p>
        <p class="card-meta">{{ (r.confirmed_at || r.started_at) | date:'medium' }} · {{ r.status }}</p>
      </div>
      <span class="chip" [ngClass]="paymentClass(r.payment_status)">{{ paymentLabel(r.payment_status) }}</span>
    </div>
    <div class="card-stats">
      <div class="stat"><span class="label">Coins</span><span class="value">{{ r.coins_transferred | number }}</span></div>
      <div class="stat"><span class="label">Fee</span><span class="value">{{ r.fee_snapshot | number }}</span></div>
      <div class="stat"><span class="label">Payable</span><span class="value">{{ r.payable_amount | number }}</span></div>
    </div>
    @if (canEdit) {
    <div class="card-actions">
      @if (r.payment_status !== 'Payed') {
      <button mat-button type="button" (click)="openEditFeeDialog(r)"><mat-icon>edit</mat-icon> Edit fee</button>
      }
      <button mat-button type="button" (click)="togglePaymentStatus(r)">
        <mat-icon>sync_alt</mat-icon> Mark {{ r.payment_status === 'Payed' ? 'unpaid' : 'paid' }}
      </button>
    </div>
    }
  </div>
  }
</div>
} @else {
<div class="table-scroll">
  <table mat-table [dataSource]="runs">
    <ng-container matColumnDef="select">
      <th mat-header-cell *matHeaderCellDef>
        @if (canSelectAll) {
        <mat-checkbox [checked]="pageAllSelected" [indeterminate]="pageSomeSelected" (change)="togglePage()"
          matTooltip="Select all unpaid runs on this page"></mat-checkbox>
        }
      </th>
      <td mat-cell *matCellDef="let r">
        <mat-checkbox [disabled]="r.payment_status === 'Payed'" [checked]="state.runs.has(r.id)"
          (change)="toggle(r)"></mat-checkbox>
      </td>
    </ng-container>
    <ng-container matColumnDef="date">
      <th mat-header-cell *matHeaderCellDef>Confirmed</th>
      <td mat-cell *matCellDef="let r">
        {{ (r.confirmed_at || r.started_at) | date:'short' }}
        <div class="sub">{{ r.status }}</div>
      </td>
    </ng-container>
    <ng-container matColumnDef="supplier">
      <th mat-header-cell *matHeaderCellDef>Supplier</th>
      <td mat-cell *matCellDef="let r">
        <span class="supplier-link" matTooltip="View buyer info"
          (click)="openSupplierInfoDialog(r.supplier_id, r.supplier_name)">{{ r.supplier_name }}</span>
      </td>
    </ng-container>
    <ng-container matColumnDef="account">
      <th mat-header-cell *matHeaderCellDef>Account</th>
      <td mat-cell *matCellDef="let r"><span class="mono">{{ r.login_info_email }}</span></td>
    </ng-container>
    <ng-container matColumnDef="coins">
      <th mat-header-cell *matHeaderCellDef>Coins</th>
      <td mat-cell *matCellDef="let r" class="num">{{ r.coins_transferred | number }}</td>
    </ng-container>
    <ng-container matColumnDef="fee">
      <th mat-header-cell *matHeaderCellDef>Fee snapshot</th>
      <td mat-cell *matCellDef="let r" class="num">
        <span class="inline-actions">
          {{ r.fee_snapshot | number }}
          @if (canEdit && r.payment_status !== 'Payed') {
          <button mat-icon-button class="mini-btn" matTooltip="Edit fee snapshot" (click)="openEditFeeDialog(r)">
            <mat-icon>edit</mat-icon>
          </button>
          }
        </span>
      </td>
    </ng-container>
    <ng-container matColumnDef="payable">
      <th mat-header-cell *matHeaderCellDef>Payable</th>
      <td mat-cell *matCellDef="let r" class="num strong">{{ r.payable_amount | number }}</td>
    </ng-container>
    <ng-container matColumnDef="payment">
      <th mat-header-cell *matHeaderCellDef>Payment</th>
      <td mat-cell *matCellDef="let r">
        <span class="inline-actions">
          <span class="chip" [ngClass]="paymentClass(r.payment_status)">{{ paymentLabel(r.payment_status) }}</span>
          @if (canEdit) {
          <button mat-icon-button class="mini-btn" matTooltip="Mark {{ r.payment_status === 'Payed' ? 'unpaid' : 'paid' }}"
            (click)="togglePaymentStatus(r)">
            <mat-icon>sync_alt</mat-icon>
          </button>
          }
        </span>
      </td>
    </ng-container>
    <tr mat-header-row *matHeaderRowDef="columns"></tr>
    <tr mat-row *matRowDef="let row; columns: columns;" [class.row-selected]="state.runs.has(row.id)"></tr>
  </table>
</div>
}

<mat-paginator
  [length]="total"
  [pageIndex]="pageIndex"
  [pageSize]="pageSize"
  [pageSizeOptions]="pageSizeOptions"
  [showFirstLastButtons]="true"
  (page)="onPage($event)">
</mat-paginator>
}
</div>
